import { DirectiveBinding } from "vue";
import { useFeatureFlags } from "../composables/useFeatureFlags";

/**
 * Vue directive for conditionally showing/hiding elements based on feature flags.
 *
 * @description
 * This directive allows you to control element visibility based on feature flag states.
 * Elements are hidden when the feature flag is disabled, and shown when enabled.
 *
 * @usage
 * Basic usage - show element when feature flag is enabled:
 * ```vue
 * <div v-feature-flag="'myFeature'">Content</div>
 * ```
 *
 * Negation - show element when feature flag is disabled (prefix with "!"):
 * ```vue
 * <div v-feature-flag="'!myFeature'">Content</div>
 * ```
 *
 * @example
 * // Show button only when 'newDashboard' feature is enabled
 * <button v-feature-flag="'newDashboard'">New Dashboard</button>
 *
 * // Show warning only when 'oldFeature' feature is disabled
 * <div v-feature-flag="'!oldFeature'" class="warning">
 *   This feature is deprecated
 * </div>
 *
 * @remarks
 * - The directive checks the feature flag state on mount and updates
 * - When negated (using "!"), the logic is inverted: element shows when flag is disabled
 * - The directive uses display: none to hide elements, preserving space in the DOM
 */
export const vFeatureFlag = {
  /**
   * Called when the element is inserted into the DOM.
   * Checks the feature flag state and hides the element if needed.
   */
  mounted(el: HTMLElement, binding: DirectiveBinding<string>) {
    const componentName = binding.value;
    const isNegated = componentName.startsWith("!");
    const actualComponentName = isNegated ? componentName.slice(1) : componentName;
    const { isEnabled } = useFeatureFlags();

    const shouldHide = isNegated ? isEnabled(actualComponentName) : !isEnabled(actualComponentName);

    if (shouldHide) {
      // Hide the element if the feature flag is disabled (or enabled when negated)
      el.style.display = "none";
    }
  },
  /**
   * Called when the binding value changes or the component updates.
   * Re-evaluates the feature flag state and updates element visibility accordingly.
   */
  updated(el: HTMLElement, binding: DirectiveBinding<string>) {
    const componentName = binding.value;
    const isNegated = componentName.startsWith("!");
    const actualComponentName = isNegated ? componentName.slice(1) : componentName;
    const { isEnabled } = useFeatureFlags();

    const shouldHide = isNegated ? isEnabled(actualComponentName) : !isEnabled(actualComponentName);

    if (shouldHide) {
      el.style.display = "none";
    } else {
      el.style.display = "";
    }
  }
};
